/**
 * Status-Kette (testerwerk#122, Hans 02.10.2026): der Weg der Meldung statt
 * nur des aktuellen Status — als Schrittfolge mit Verbindungslinien:
 *
 *   (Neu)──(Gesichtet)──(In Umsetzung)──(Lösung bereitgestellt)──[Melderfreigabe · wartet auf DEV]
 *
 * Erledigte Schritte gefüllt und gedämpft, der aktuelle Schritt farbig und
 * fett mit „wartet auf DEV/Melder“. Farben nach Art: Rückfragen gelb,
 * Bereitstellung blau, Freigaben grün. Kompakt (Listen) wird erst ab sieben
 * Schritten in der Mitte mit „…“ gekürzt.
 *
 * Jeder Schritt zweizeilig (Hans 06.10.2026): oben die Bezeichnung, darunter
 * Datum und Uhrzeit des Schritts — beide Zeilen linksbündig.
 */

import { Fragment, useLayoutEffect, useRef, useState } from "react"
import { STATUS_FARBE, ZUG_LABEL, statusMitZug } from "@/lib/labels"
import type { KettenSchritt, ReportStatus } from "@/types"

export type SchrittArt = "neutral" | "rueckfrage" | "bereit" | "freigabe" | "ende"
type Art = SchrittArt

/** Art eines Kettenschritts (Farbe des Akzents/Punkts) — auch für den Zeitstrahl */
export function schrittArt(schluessel: string): SchrittArt {
  return ART[schluessel] ?? "neutral"
}

const ART: Record<string, Art> = {
  rueckfrage_melder: "rueckfrage",
  rueckfrage_beantwortet: "rueckfrage",
  melderrueckfrage: "rueckfrage",
  rueckfrage_umsetzung: "rueckfrage",
  wiedergeoeffnet: "rueckfrage",
  bereitgestellt: "bereit",
  rueckfrage_bereitgestellt: "bereit",
  melderfreigabe: "freigabe",
  dev_freigabe: "freigabe",
  abgelehnt: "ende",
  verworfen: "ende",
  zusammengefuehrt: "ende",
}

/** Alle Schritte im gleichen hellen Grau (Hans 02.10.2026); die Art zeigt
 * nur der farbige Akzent links am aktuellen Schritt. */
const GRAU = "border-slate-300 bg-slate-200 text-slate-700"
const AKZENT: Record<Art, string> = {
  neutral: "border-l-primary",
  rueckfrage: "border-l-warning",
  bereit: "border-l-sky-500",
  freigabe: "border-l-success",
  ende: "border-l-destructive",
}

const MAX_KOMPAKT = 6

/**
 * Zeilenumbruch-Linien (Hans 02.10.2026): Bricht die vollständige Kette um,
 * verbindet eine Linie das Ende der Zeile mit dem nächsten Schritt — vom
 * Zeilenende nach unten, zwischen den Zeilen zurück nach vorn, eine Zeile
 * tiefer nach rechts bis zum nächsten Schritt. Gemessen wird im Browser,
 * die Linie folgt also jeder Fensterbreite.
 */
function useUmbruchLinien(aktiv: boolean) {
  const ref = useRef<HTMLDivElement>(null)
  const [pfade, setPfade] = useState<string[]>([])
  useLayoutEffect(() => {
    const box = ref.current
    if (!aktiv || !box) return
    const messen = () => {
      const basis = box.getBoundingClientRect()
      const felder = Array.from(box.querySelectorAll<HTMLElement>("[data-schritt]")).map((el) => {
        const r = el.getBoundingClientRect()
        return {
          links: r.left - basis.left,
          rechts: r.right - basis.left,
          oben: r.top - basis.top,
          unten: r.bottom - basis.top,
          mitte: r.top - basis.top + r.height / 2,
        }
      })
      const neu: string[] = []
      for (let i = 1; i < felder.length; i++) {
        const a = felder[i - 1]
        const b = felder[i]
        if (b.oben < a.unten) continue // gleiche Zeile
        const rechtsAussen = basis.width + 8
        const linksAussen = -10
        const zwischen = (a.unten + b.oben) / 2
        neu.push(
          `M ${a.rechts} ${a.mitte} H ${rechtsAussen} V ${zwischen} H ${linksAussen} V ${b.mitte} H ${b.links}`
        )
      }
      setPfade((alt) => (alt.join("|") === neu.join("|") ? alt : neu))
    }
    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(box)
    return () => beobachter.disconnect()
  }, [aktiv])
  return { ref, pfade }
}

interface Props {
  report: { status: ReportStatus; zug?: "dev" | "melder" | null; kette?: KettenSchritt[] }
  /** true: Listen-Darstellung, ab sieben Schritten in der Mitte gekürzt */
  kompakt?: boolean
}

export function StatusKette({ report, kompakt = false }: Props) {
  const kette = report.kette ?? []
  const { ref, pfade } = useUmbruchLinien(!kompakt && kette.length > 1)
  if (kette.length === 0) {
    return (
      <span
        className={`inline-flex shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
      >
        {statusMitZug(report)}
      </span>
    )
  }

  const letzter = kette[kette.length - 1]
  const davor = kette.slice(0, -1)
  // Kompakt: erstes Glied, „…“, die letzten vier vor dem aktuellen
  const kuerzen = kompakt && kette.length > MAX_KOMPAKT
  const ausgelassen = kuerzen ? davor.slice(1, davor.length - (MAX_KOMPAKT - 2)) : []
  const sichtbar: (KettenSchritt | "…")[] = kuerzen
    ? [davor[0], "…", ...davor.slice(davor.length - (MAX_KOMPAKT - 2))]
    : davor

  const zeit = (s: KettenSchritt) =>
    s.zeit
      ? new Date(s.zeit).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })
      : undefined
  /** Zweite Zeile: Datum und Uhrzeit, kleiner und leiser als die Bezeichnung */
  const zeitZeile = (s: KettenSchritt) =>
    s.zeit ? (
      <span
        className={`block font-normal tabular-nums opacity-70 ${kompakt ? "text-[10px]" : "text-[11px]"}`}
      >
        {zeit(s)?.replace(",", "")}
      </span>
    ) : null
  const art = (s: KettenSchritt): Art => ART[s.schluessel] ?? "neutral"
  // Hans 14.10.2026: Hover auf einen Schritt zeigt die auslösende Kommunikation
  // (Autor + Auszug) bzw. die Begründung des Statuswechsels.
  const hinweis = (s: KettenSchritt, rechts = false) =>
    s.hinweis ? (
      <span
        role="tooltip"
        className={`pointer-events-none absolute top-full z-40 mt-1.5 hidden w-72 whitespace-normal rounded-md border border-slate-300 bg-card px-2.5 py-2 text-left text-[11.5px] font-normal leading-snug text-foreground shadow-card group-hover:block ${
          rechts ? "right-0" : "left-0"
        }`}
      >
        {s.hinweis}
      </span>
    ) : null
  const linie = <span aria-hidden className="h-px w-4 shrink-0 bg-slate-400/60" />
  const feld =
    "inline-flex flex-col items-start whitespace-nowrap rounded-md border px-2.5 py-1 text-left leading-tight"

  // Rechtsbündiger letzter Schritt mit fester Breite: über alle Karten
  // hinweg stehen die aktuellen Schritte sauber untereinander; die Linie
  // davor wächst oder schrumpft mit der Länge der Kette.
  return (
    <div ref={ref} className="relative">
      {pfade.length > 0 && (
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible text-slate-400/60"
        >
          {pfade.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="currentColor" strokeWidth={1} />
          ))}
        </svg>
      )}
    <ol
      aria-label="Weg der Meldung"
      className={`relative flex w-full flex-wrap items-center ${
        kompakt ? "gap-y-1.5 text-[11.5px]" : "gap-y-5 text-[12.5px]"
      }`}
    >
      {sichtbar.map((s, i) => (
        <Fragment key={i}>
          <li className="flex items-center">
            {s === "…" ? (
              <span
                data-schritt
                className={`${feld} border-dashed border-slate-400 text-slate-400`}
                title={ausgelassen.map((a) => a.label).join(" › ")}
              >
                … {ausgelassen.length} weitere
              </span>
            ) : (
              <span data-schritt className={`${feld} group relative font-medium ${GRAU}`}>
                <span>{s.label}</span>
                {zeitZeile(s)}
                {hinweis(s)}
              </span>
            )}
          </li>
          {i < sichtbar.length - 1 && (
            <li aria-hidden className="flex items-center">
              {linie}
            </li>
          )}
        </Fragment>
      ))}
      {/* Wachsende Linie: schiebt den aktuellen Schritt immer nach rechts —
          auch wenn er der einzige ist */}
      <li aria-hidden className="flex min-w-4 flex-1 items-center">
        <span className={`h-px w-full ${sichtbar.length > 0 ? "bg-slate-400/60" : ""}`} />
      </li>
      <li className="ml-auto flex items-center" aria-current="step">
        <span
          data-schritt
          className={`${feld} ${GRAU} group relative border-l-4 font-bold shadow-sm ${AKZENT[art(letzter)]} ${
            kompakt ? "w-[23rem] justify-center" : ""
          }`}
        >
          <span className={kompakt ? "max-w-full truncate" : ""}>
            {letzter.label}
            {report.zug ? ` · ${ZUG_LABEL[report.zug]}` : ""}
          </span>
          {zeitZeile(letzter)}
          {hinweis(letzter, true)}
        </span>
      </li>
    </ol>
    </div>
  )
}
